// CRM panel of the agent's call page: who is calling (screen pop pushed over the WebSocket as crm.screenpop, the
// API's answer first, the CRM's a moment later), search, link the call to a record, create a lead or contact for an
// unknown caller, add a follow-up task, and dial a record's number (click-to-call). Every action goes through
// /api/v1/crm/agent, which checks that the call is the agent's own; the browser holds no CRM token and never
// sends an engine command of its own.
import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, Link2, Phone, ExternalLink, UserPlus, ListTodo, Loader2, CheckCircle2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { onLiveEvent, useRealtime, type LiveEvent } from '@/store/realtime';
import { usePhone } from '@/store/phone';
import { useCan } from '@/store/auth';
import { toast } from '@/store/toast';
import { ErrorBox, Field } from '@/components/common';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Select, Textarea } from '@/components/ui/input';
import { Badge, StatusBadge } from '@/components/ui/badge';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { enumLabel } from '@/lib/utils';
import { t } from '@/i18n';
import { crmErrorText, type RecordCard } from '@/pages/admin/crm/types';

const A = '/v1/crm/agent';
interface Context { connection: null | { id: number; provider: string; name: string; status: string; enabled: boolean; screen_pop: boolean;
  unknown_caller: { create: string; object?: 'LEAD' | 'CONTACT' } } }
interface CallInfo { call: { call_uuid: string; direction: string; number: string | null };
  link: null | { record_object: string | null; record_remote_id: string | null; link_origin: string; sync_status: string; record: RecordCard | null } }
interface Pop { status: string; matches: RecordCard[]; pending?: boolean; number?: string | null }

const popOf = (e: LiveEvent): Pop => ({ status: String(e.data.status), matches: (e.data.matches ?? []) as RecordCard[], pending: !!e.data.pending, number: e.data.number });
const newKey = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`).replace(/[^A-Za-z0-9_.:-]/g, '');

/** callUuid: the call on screen (kept by the caller through wrap-up); campaigns: where click-to-call may dial from */
export function CrmPanel({ callUuid, campaigns }: { callUuid: string | null; campaigns: { id: number; name: string }[] }) {
  const canLookup = useCan('crm_agent.lookup');
  const ctx = useQuery({ queryKey: ['crm-agent-context'], queryFn: () => api<Context>(`${A}/context`), enabled: canLookup, staleTime: 120_000, retry: false });
  const conn = ctx.data?.connection;
  if (!canLookup || !conn || !conn.enabled) return null;
  return <Panel conn={conn} callUuid={callUuid} campaigns={campaigns} />;
}

function Panel({ conn, callUuid, campaigns }: { conn: NonNullable<Context['connection']>; callUuid: string | null; campaigns: { id: number; name: string }[] }) {
  const qc = useQueryClient();
  const [pop, setPop] = React.useState<Pop | null>(null);
  const [results, setResults] = React.useState<RecordCard[] | null>(null);
  const [creating, setCreating] = React.useState(false);
  const [task, setTask] = React.useState<RecordCard | 'call' | null>(null);
  const info = useQuery({ queryKey: ['crm-agent-call', callUuid], enabled: !!callUuid, retry: false,
                          queryFn: () => api<CallInfo>(`${A}/calls/${callUuid}`) });
  const canCreate = useCan('crm_agent.create_record') && conn.unknown_caller.create !== 'NEVER';
  const canTask = useCan('crm_agent.create_task');

  // a new call: the pop that already arrived for it (the page may open after the event), else nothing yet
  React.useEffect(() => {
    setResults(null);
    const last = callUuid ? useRealtime.getState().recent.find((e) => e.event === 'crm.screenpop' && e.data.call_uuid === callUuid) : undefined;
    setPop(last ? popOf(last) : null);
  }, [callUuid]);
  React.useEffect(() => onLiveEvent((e) => {
    if (!callUuid || e.data.call_uuid !== callUuid) return;
    if (e.event === 'crm.screenpop') setPop(popOf(e));
    if (e.event === 'crm.screenpop' || e.event === 'crm.activity') qc.invalidateQueries({ queryKey: ['crm-agent-call', callUuid] });
  }), [callUuid, qc]);

  async function link(r: RecordCard) {
    if (!callUuid) return;
    try { await api(`${A}/link`, { body: { call_uuid: callUuid, object: r.object, id: r.id } }); toast.ok(t('Call linked to {{name}}', { name: r.name })); info.refetch(); }
    catch (e) { toast.error(crmErrorText(e)); }
  }

  const linked = info.data?.link?.record ?? null;
  const shown = results ?? pop?.matches ?? [];
  return (
    <Card data-testid="crm-panel">
      <CardHeader className="flex-row items-start justify-between gap-2">
        <div><CardTitle>{t('CRM')}</CardTitle><CardDescription>{conn.name}</CardDescription></div>
        {info.data?.link && <span className="flex items-center gap-1 text-xs text-muted-foreground">{t('Logging')} <StatusBadge value={info.data.link.sync_status} /></span>}
      </CardHeader>
      <CardContent className="grid gap-3">
        {linked && <div className="rounded-lg border border-success/40 p-2" data-testid="crm-linked">
          <div className="mb-1 flex items-center gap-1 text-xs text-success"><CheckCircle2 className="h-3 w-3" /> {t('This call is about')}</div>
          <RecordRow r={linked} campaigns={campaigns} onTask={canTask ? () => setTask(linked) : undefined} />
        </div>}
        {callUuid && pop?.pending && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t('Searching the CRM…')}</div>}
        {callUuid && pop && !pop.pending && pop.status === 'NONE' && !results && <p className="text-sm text-muted-foreground" data-testid="crm-no-match">{t('No CRM record has the number {{number}}.', { number: pop.number ?? '' })}</p>}
        {callUuid && pop?.status === 'ERROR' && <p className="text-sm text-destructive">{t('The CRM could not be searched; try the search below.')}</p>}
        <SearchBox onResults={setResults} initial={info.data?.call.number ?? ''} />
        <div className="grid gap-2" data-testid="crm-matches">
          {shown.filter((r) => !(linked && r.id === linked.id)).map((r) => (
            <div key={`${r.object}:${r.id}`} className="rounded-lg border p-2">
              <RecordRow r={r} campaigns={campaigns} onLink={callUuid ? () => link(r) : undefined} onTask={canTask ? () => setTask(r) : undefined} />
            </div>
          ))}
          {results && !results.length && <p className="text-sm text-muted-foreground">{t('Nothing found.')}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          {canCreate && <Button size="sm" variant="outline" onClick={() => setCreating(true)}><UserPlus /> {conn.unknown_caller.object === 'CONTACT' ? t('New contact') : t('New lead')}</Button>}
          {canTask && callUuid && <Button size="sm" variant="outline" onClick={() => setTask('call')}><ListTodo /> {t('Follow-up task')}</Button>}
        </div>
      </CardContent>
      <Dialog open={creating} onOpenChange={setCreating}>
        {creating && <DialogContent title={conn.unknown_caller.object === 'CONTACT' ? t('New contact') : t('New lead')} className="max-w-md">
          <CreateRecord type={conn.unknown_caller.object ?? 'LEAD'} callUuid={callUuid} phone={info.data?.call.number ?? pop?.number ?? ''}
            onDone={() => { setCreating(false); info.refetch(); }} />
        </DialogContent>}
      </Dialog>
      <Dialog open={!!task} onOpenChange={(o) => !o && setTask(null)}>
        {task && <DialogContent title={t('Follow-up task')} className="max-w-md">
          <TaskForm callUuid={callUuid} record={task === 'call' ? null : task} onDone={() => setTask(null)} />
        </DialogContent>}
      </Dialog>
    </Card>
  );
}

function RecordRow({ r, campaigns, onLink, onTask }: { r: RecordCard; campaigns: { id: number; name: string }[]; onLink?: () => void; onTask?: () => void }) {
  const [dialing, setDialing] = React.useState(false);
  return (
    <div className="grid gap-1 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{r.name || t('Unknown name')}</span>
        <Badge variant="outline">{r.object === 'local' ? t('Contact list') : enumLabel(r.type)}</Badge>
        {r.company && <span className="text-muted-foreground">{r.company}</span>}
      </div>
      <div className="flex flex-wrap gap-x-3 text-xs text-muted-foreground">
        {r.phones.map((p) => <span key={p.field + p.number} className="tabular-nums">{p.number}</span>)}{r.email && <span>{r.email}</span>}
      </div>
      <div className="flex flex-wrap gap-1">
        {onLink && r.object !== 'local' && <Button size="sm" variant="outline" onClick={onLink}><Link2 /> {t('Link to this call')}</Button>}
        {r.object !== 'local' && r.phones.length > 0 && <Button size="sm" variant="outline" onClick={() => setDialing(true)}><Phone /> {t('Call')}</Button>}
        {onTask && r.object !== 'local' && <Button size="sm" variant="ghost" onClick={onTask}><ListTodo /> {t('Task')}</Button>}
        {r.url && <Button size="sm" variant="ghost" asChild><a href={r.url} target="_blank" rel="noopener noreferrer"><ExternalLink /> {t('Open in CRM')}</a></Button>}
      </div>
      <Dialog open={dialing} onOpenChange={setDialing}>
        {dialing && <DialogContent title={t('Call {{name}}', { name: r.name })} className="max-w-sm"><ClickToCall r={r} campaigns={campaigns} onDone={() => setDialing(false)} /></DialogContent>}
      </Dialog>
    </div>
  );
}

function SearchBox({ onResults, initial }: { onResults: (r: RecordCard[] | null) => void; initial: string }) {
  const [q, setQ] = React.useState(initial);
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { setQ(initial); }, [initial]);
  async function search(e: React.FormEvent) {
    e.preventDefault();
    const s = q.trim();
    if (!s) { onResults(null); return; }
    const body = /@/.test(s) ? { email: s, remote: true } : /^[+\d\s().-]{5,}$/.test(s) ? { phone: s, remote: true } : { query: s };
    setBusy(true);
    try { onResults((await api<{ matches: RecordCard[] }>(`${A}/lookup`, { body })).matches); }
    catch (err) { toast.error(crmErrorText(err)); }
    finally { setBusy(false); }
  }
  return (
    <form onSubmit={search} className="flex gap-2">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('Phone, e-mail or name')} aria-label={t('Search the CRM')} />
      <Button type="submit" variant="outline" disabled={busy}>{busy ? <Loader2 className="animate-spin" /> : <Search />} {t('Search')}</Button>
    </form>
  );
}

function ClickToCall({ r, campaigns, onDone }: { r: RecordCard; campaigns: { id: number; name: string }[]; onDone: () => void }) {
  const phone = usePhone();
  const [number, setNumber] = React.useState(r.phones[0]?.number ?? '');
  const [campaign, setCampaign] = React.useState('');
  const [error, setError] = React.useState<unknown>(null);
  async function dial(e: React.FormEvent) {
    e.preventDefault(); setError(null);
    phone.expectCall();                                // the engine rings this phone first
    try {
      await api(`${A}/click-to-call`, { body: { object: r.object, id: r.id, number, campaign_id: campaign ? Number(campaign) : undefined } });
      toast.ok(t('Calling {{number}}', { number })); onDone();
    } catch (err) { phone.expectCall(0); setError(err instanceof ApiError && err.body?.message ? new Error(crmErrorText(err)) : err); }
  }
  return (
    <form onSubmit={dial} className="grid gap-3">
      <Field help="crmdial.number" label={t('Number')}><Select value={number} onChange={(e) => setNumber(e.target.value)}>
        {r.phones.map((p) => <option key={p.field + p.number} value={p.number}>{p.number}{p.field ? ` (${p.field})` : ''}</option>)}
      </Select></Field>
      <Field help="crmdial.campaign" label={t('Campaign')}><Select value={campaign} onChange={(e) => setCampaign(e.target.value)}>
        <option value="">{t('No campaign')}</option>{campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </Select></Field>
      <ErrorBox error={error} />
      <Button type="submit" variant="success" disabled={phone.reg !== 'registered'}><Phone /> {t('Dial')}</Button>
      {phone.reg !== 'registered' && <span className="text-xs text-muted-foreground">{t('Your softphone must be registered to dial.')}</span>}
    </form>
  );
}

function CreateRecord({ type, callUuid, phone, onDone }: { type: 'LEAD' | 'CONTACT'; callUuid: string | null; phone: string; onDone: () => void }) {
  const [key] = React.useState(newKey);                 // a double click or a retry is the same record
  const [v, setV] = React.useState({ first_name: '', last_name: '', company: '', email: '', phone });
  const [error, setError] = React.useState<unknown>(null);
  const [busy, setBusy] = React.useState(false);
  const set = (k: keyof typeof v) => (e: React.ChangeEvent<HTMLInputElement>) => setV((x) => ({ ...x, [k]: e.target.value }));
  async function save(e: React.FormEvent) {
    e.preventDefault(); setError(null); setBusy(true);
    const body: Record<string, unknown> = { type, last_name: v.last_name, idempotency_key: key };
    for (const k of ['first_name', 'company', 'email', 'phone'] as const) if (v[k].trim()) body[k] = v[k].trim();
    if (callUuid) body.call_uuid = callUuid;
    try { await api(`${A}/records`, { body }); toast.ok(t('Saved in the CRM')); onDone(); }
    catch (err) { setError(new Error(crmErrorText(err))); }
    finally { setBusy(false); }
  }
  return (
    <form onSubmit={save} className="grid gap-3">
      <div className="grid grid-cols-2 gap-3">
        <Field help="crmrecord.first_name" label={t('First name')}><Input value={v.first_name} onChange={set('first_name')} maxLength={64} /></Field>
        <Field help="crmrecord.last_name" label={t('Last name')}><Input value={v.last_name} onChange={set('last_name')} maxLength={80} required /></Field>
      </div>
      <Field help="crmrecord.company" label={t('Company')}><Input value={v.company} onChange={set('company')} maxLength={255} required={type === 'LEAD'} /></Field>
      <Field help="crmrecord.phone" label={t('Phone')}><Input value={v.phone} onChange={set('phone')} maxLength={40} /></Field>
      <Field help="crmrecord.email" label={t('E-mail')}><Input type="email" value={v.email} onChange={set('email')} maxLength={254} /></Field>
      <ErrorBox error={error} />
      <div className="flex justify-end"><Button type="submit" disabled={busy}>{t('Save')}</Button></div>
    </form>
  );
}

function TaskForm({ callUuid, record, onDone }: { callUuid: string | null; record: RecordCard | null; onDone: () => void }) {
  const [key] = React.useState(newKey);
  const [v, setV] = React.useState({ subject: '', notes: '', due: '', priority: 'NORMAL' });
  const [error, setError] = React.useState<unknown>(null);
  async function save(e: React.FormEvent) {
    e.preventDefault(); setError(null);
    const body: Record<string, unknown> = { subject: v.subject, priority: v.priority, idempotency_key: key };
    if (v.notes.trim()) body.notes = v.notes.trim();
    if (v.due) body.due_at = new Date(v.due).toISOString();
    if (callUuid) body.call_uuid = callUuid;
    if (record) { body.object = record.object; body.id = record.id; }
    try { await api(`${A}/tasks`, { body }); toast.ok(t('The task is on its way to the CRM')); onDone(); }
    catch (err) { setError(new Error(crmErrorText(err))); }
  }
  return (
    <form onSubmit={save} className="grid gap-3">
      {record && <p className="text-sm text-muted-foreground">{t('For {{name}}', { name: record.name })}</p>}
      <Field help="crmtask.subject" label={t('Subject')}><Input value={v.subject} onChange={(e) => setV((x) => ({ ...x, subject: e.target.value }))} maxLength={255} required /></Field>
      <Field help="crmtask.due" label={t('Due')} hint={t('Your local time.')}><Input type="datetime-local" value={v.due} onChange={(e) => setV((x) => ({ ...x, due: e.target.value }))} /></Field>
      <Field help="crmtask.priority" label={t('Priority')}><Select value={v.priority} onChange={(e) => setV((x) => ({ ...x, priority: e.target.value }))}>
        {['LOW', 'NORMAL', 'HIGH'].map((x) => <option key={x} value={x}>{enumLabel(x)}</option>)}
      </Select></Field>
      <Field help="crmtask.notes" label={t('Notes')}><Textarea value={v.notes} onChange={(e) => setV((x) => ({ ...x, notes: e.target.value }))} rows={3} maxLength={2000} /></Field>
      <ErrorBox error={error} />
      <div className="flex justify-end"><Button type="submit">{t('Save')}</Button></div>
    </form>
  );
}
